<template>
  <Dialog
    v-model:visible="displayModal"
    class="p-dialog-maximized rcs-visual-crop-modal"
    :header="modalTitle"
    :maximizable="true"
    style="width: 80rem"
    @update:visible="handleClose"
  >
    <div
      v-if="processing"
      class="rcs-visual-crop-modal__loading"
    >
      <i
        class="fa-light fa-spinner-third fa-spin"
        aria-hidden="true"
      />
      <span>{{ t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.processing') }}</span>
    </div>
    <iframe
      v-show="!processing"
      sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts"
      class="pixie-wrapper"
      :srcdoc="pixieSrcDoc"
      @load="iframeLoaded"
    />
  </Dialog>
</template>

<script lang="ts">
import Dialog from 'primevue/dialog';
import {
  computed,
  defineComponent,
  onBeforeMount,
  PropType,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import usePixieEditor from '@/composables/usePixieEditor';
import { uploadFileToMedia } from '@/composables/configs/configuration';
import { UserState } from '@/composables/User';
import type { RcsVisualImagePreset } from '@/utils/rcs-visual-image';
import {
  blobToDataUrl,
  normalizeUploadedMediaUrl,
  processRcsVisualImageForUpload,
  RCS_VISUAL_IMAGE_PRESETS,
} from '@/utils/rcs-visual-image';

export default defineComponent({
  name: 'RcsVisualImageCropModal',

  components: {
    Dialog,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },

    imageUrl: {
      type: String,
      required: true,
    },

    preset: {
      type: String as PropType<RcsVisualImagePreset>,
      required: true,
    },
  },

  emits: ['on-close-dialog', 'on-save'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const toast = useToast();
    const processing = ref(false);
    const imageUrl = ref(props.imageUrl);
    const content = ref<Document | null>(null);
    const pixieSrcDoc = computed(
      () => content.value && content.value.documentElement.outerHTML,
    );

    const { showPixieEditor, destroyPixieEditor } = usePixieEditor();

    const displayModal = computed(() => props.displayDialog);

    const spec = computed(() => RCS_VISUAL_IMAGE_PRESETS[props.preset]);

    const modalTitle = computed(() => (
      props.preset === 'logo'
        ? t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.titleLogo')
        : t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.titleBanner')
    ));

    const cropPresetLabel = computed(() => (
      props.preset === 'logo'
        ? t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.presetLogo')
        : t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.presetBanner')
    ));

    const handleClose = () => {
      destroyPixieEditor();
      emit('on-close-dialog');
    };

    const showError = (key: string) => {
      toast.add({
        severity: 'error',
        summary: t('error'),
        detail: t(`storeParameters.services.rcsServiceParameter.wizard.visual.errors.${key}`),
        life: 4000,
      });
    };

    const handleSave = async (data: string) => {
      processing.value = true;
      try {
        const blob = await processRcsVisualImageForUpload(data, props.preset);
        const dataUrl = await blobToDataUrl(blob);
        const uploadResult = await uploadFileToMedia(
          dataUrl,
          UserState.activeShop?.id ?? 0,
        );

        const publicUrl = normalizeUploadedMediaUrl(uploadResult?.data);
        if (!publicUrl) {
          showError('uploadFailed');
          return;
        }

        emit('on-save', publicUrl);
        handleClose();
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message === 'file_too_large') {
          showError('fileTooLarge');
        } else {
          showError('uploadFailed');
        }
      } finally {
        processing.value = false;
      }
    };

    const iframeLoaded = () => {
      const pixieIframe = (
        document.querySelector('iframe.pixie-wrapper') as HTMLIFrameElement
      )?.contentWindow;

      if (!pixieIframe) {
        return;
      }

      showPixieEditor(pixieIframe, imageUrl.value, handleSave, {
        cropRatio: spec.value.cropRatio,
        cropPresetName: cropPresetLabel.value,
        openCropOnLoad: true,
      });
    };

    watch(
      () => props.imageUrl,
      (value) => {
        imageUrl.value = value;
      },
    );

    onBeforeMount(() => {
      const spmApiJs = document.createElement('script');
      spmApiJs.setAttribute('src', '/libs/pixiev2/dist/pixie.umd.js');
      content.value = document.implementation.createHTMLDocument();

      const baseIframeCss = document.createElement('link');
      baseIframeCss.setAttribute('rel', 'stylesheet');
      baseIframeCss.setAttribute('href', '/css/template-builder/baseIframePixie.css');
      content.value.head.appendChild(baseIframeCss);

      const pixieContainer = document.createElement('div');
      pixieContainer.setAttribute('id', 'editor-container');
      content.value.body.appendChild(pixieContainer);
      content.value.body.appendChild(spmApiJs);
    });

    return {
      t,
      displayModal,
      modalTitle,
      processing,
      handleClose,
      iframeLoaded,
      pixieSrcDoc,
    };
  },
});
</script>

<style lang="scss" scoped>
.rcs-visual-crop-modal__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 320px;
  color: var(--text-color-secondary);
}
</style>
